// src/components/pipelines/sheets/OnEntrySection.jsx
import React, { useState } from 'react';
import { Play, Plus, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../../ui/button.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Card } from '../../ui/card.tsx';
import { usePipelines } from '../../../hooks/usePipelines';
import ActionBuilder from './ActionBuilder';

/**
 * OnEntrySection - Configures actions that fire when customer enters the stage
 *
 * Each action creates an AutomationRule with trigger_type='on_entry'
 */
const OnEntrySection = ({ stage, rules, isLoading }) => {
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const { createRule, deleteRule } = usePipelines();

  const handleAddAction = (actionData) => {
    // Create AutomationRule with trigger_type='on_entry'
    const ruleData = {
      name: actionData.name || `On Entry: ${actionData.type}`,
      trigger_type: 'on_entry',
      trigger_event: null,
      conditions: [],
      actions: [actionData], // Single action wrapped in array
      next_stage_id: null,
      priority: 0,
      is_active: true
    };

    createRule(stage.id, ruleData);
    setIsAddingAction(false);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this action?')) {
      deleteRule(ruleId);
    }
  };

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <div className="w-full flex items-center justify-between p-2 bg-green-500/10 border border-green-500/20 rounded-md">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-green-400" />
          <h3 className="text-xs font-medium text-green-100">Entry</h3>
        </div>
        <Badge variant="outline" className="text-xs border-green-500/50 text-green-300 h-4 px-1">
          {rules.length}
        </Badge>
      </div>

      <div className="space-y-3">
          {/* Existing Actions */}
          {rules.length > 0 && (
            <div className="space-y-2 pl-3">
              {rules.map((rule) => (
                <Card key={rule.id} className="p-3 bg-slate-800/50 border-slate-700/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-slate-200">{rule.name}</h4>
                        {!rule.is_active && (
                          <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      {rule.actions && rule.actions.length > 0 && (
                        <div className="space-y-1">
                          {rule.actions.map((action, idx) => (
                            <div key={idx} className="text-xs text-slate-400">
                              • {action.type.replace(/_/g, ' ')}
                              {action.template_id && ` (Template: ${action.template_id.slice(0, 8)}...)`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-red-400"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Add Action Button */}
          {!isAddingAction && (
            <div className="pl-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 border-green-500/30 text-green-300 hover:bg-green-500/10 hover:text-green-200 text-xs"
                onClick={() => setIsAddingAction(true)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>
          )}

          {/* Action Builder */}
          {isAddingAction && (
            <div className="pl-3">
              <ActionBuilder
                onSave={handleAddAction}
                onCancel={() => setIsAddingAction(false)}
              />
            </div>
          )}

          {/* Loading State */}
          {isLoading && rules.length === 0 && (
            <div className="pl-3 text-xs text-slate-500 italic">
              Loading...
            </div>
          )}
      </div>
    </div>
  );
};

export default OnEntrySection;
